import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash, faSpinner, faArchive } from '@fortawesome/free-solid-svg-icons';
import { useAuth, PATIENT_DIARY_LAYOUT_SIDEBAR, PATIENT_DIARY_LAYOUT_TAB } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';
import DetailsTab from './tabs/DetailsTab';
import NotesTab from './tabs/NotesTab';
import TranscriptTab from './tabs/TranscriptTab';
import CodesTab from './tabs/CodesTab';
import MeasurementsTab from './tabs/MeasurementsTab';
import OutcomeTasksTab from './tabs/OutcomeTasksTab';
import ActionsTab from './tabs/ActionsTab';
import PatientDiaryPanel from '../detail/PatientDiaryPanel';
import FileAwayModal from '../detail/FileAwayModal';
import useShouldBundleBloodPressureReadings from '../detail/measurements/useShouldBundleBloodPressureReadings';
import usePendingReferralModal from '../detail/usePendingReferralModal';
import ReferralModal from '../referrals/ReferralModal';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import ConsultationToolbar from './ConsultationToolbar';
import DeleteConsultationModal from './DeleteConsultationModal';
import MicInterruptBanner from './MicInterruptBanner';
import UploadFailureBanner from './UploadFailureBanner';
import useConsultationRecord from './useConsultationRecord';
import useConsultationPatient from './useConsultationPatient';
import useConsultationRecording from './useConsultationRecording';
import useConsultationAudio from './useConsultationAudio';
import useConsultationDelete from './useConsultationDelete';
import useConsultationFileAway from './useConsultationFileAway';
import useConsultationSidebar from './useConsultationSidebar';
import useConsultationNavigation from './useConsultationNavigation';
import useConsultationLiveUpdates from './useConsultationLiveUpdates';
import '../detail/DocumentDetailView.css';
import './ConsultationDetailView.css';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';

// Show audio player once recording is complete
const AUDIO_STATUSES = [
  ConsultationStatusEnum.PENDING.value,
  ConsultationStatusEnum.GENERATING.value,
  ConsultationStatusEnum.READY.value,
  ConsultationStatusEnum.QUEUED_FOR_FILING.value,
  ConsultationStatusEnum.FILED_AWAY.value,
];

const ConsultationDetailViewController = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const shouldBundleBloodPressureReadings = useShouldBundleBloodPressureReadings();

  const record = useConsultationRecord({ consultationId, user });
  const { consultation, consultationStatus, consultationService, updateConsultation } = record;

  const patient = useConsultationPatient({ consultationId, consultation, consultationService });
  const recording = useConsultationRecording({
    consultationId,
    consultationStatus,
    consultationService,
    reloadConsultation: record.reloadConsultation,
    navigate,
  });
  const showAudioPlayer = AUDIO_STATUSES.includes(consultationStatus);
  const audio = useConsultationAudio({ consultationId, consultationService, enabled: showAudioPlayer });
  const deletion = useConsultationDelete({
    consultationId, consultationService, discardRecording: recording.discardRecording, navigate,
  });
  const fileAway = useConsultationFileAway({ consultationId, consultation, consultationService, updateConsultation });
  const referral = usePendingReferralModal({
    loadKey: consultationId,
    removePendingReferralLocally: patient.removePendingReferralLocally,
    applyPendingReferralPatch: patient.applyPendingReferralPatch,
  });
  const sidebar = useConsultationSidebar({
    consultation, location, consultationService, updateConsultation, isEditable: record.isEditable,
  });
  const navigation = useConsultationNavigation({
    consultationId,
    location,
    navigate,
    backPatientId: patient.patientData?.patientId,
    confirmLeaveRecording: recording.confirmLeaveRecording,
  });
  useConsultationLiveUpdates({
    consultationId,
    consultationStatus,
    user,
    consultationService,
    updateConsultation,
    clearLoadError: record.clearLoadError,
    navigate,
    onFiledAwayByCurrentUser: navigation.requestAutoNavigate,
    refetchPatient: patient.refetchPatient,
  });

  const { activeUsers, teams, isEditable, isRegenerationEnabled, handleRegenerationClick, regenerateService } = record;
  const { activeMainTab, setActiveMainTab, activeSidebarTab, setActiveSidebarTab, patientDiaryLayout } = sidebar;

  if (record.loading) {
    return (
      <div className="consultation-detail-view">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading consultation...</p>
        </div>
      </div>
    );
  }

  if (record.error || !consultation) {
    return (
      <div className="consultation-detail-view">
        <div className="error-container">
          <h2>Error</h2>
          <p>{record.error || 'Consultation not found'}</p>
          <button onClick={navigation.handleBackClick}>{navigation.backLabel}</button>
        </div>
      </div>
    );
  }

  const statusEnum = ConsultationStatusEnum[consultationStatus] || ConsultationStatusEnum.PENDING;
  const showFileAwayPatientHint = !record.canFileAway && isEditable && !fileAway.fileAwayLoading;
  const showRecordingBanners = recording.isRecording && !recording.finalizePending;

  return (
    <div className="consultation-detail-view">
      {/* Header Row 1 - matches document-header */}
      <div className="consultation-header">
        <div className="header-left">
          <button className="header-button" onClick={navigation.handleBackClick}>
            <FontAwesomeIcon icon={faArrowLeft} /> {navigation.backLabel}
          </button>
        </div>
        <div className="consultation-actions">
          <button
            className="header-button header-button-delete"
            onClick={deletion.openDeleteModal}
            disabled={!record.canDelete}
            title={record.canDelete ? "Delete Consultation" : "Cannot delete in current state"}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          {consultationStatus !== ConsultationStatusEnum.FILED_AWAY.value && (
            <div className={`file-away-button-wrapper ${showFileAwayPatientHint ? 'file-away-disabled-patient' : ''}`}>
              <button
                className="header-button header-button-primary"
                onClick={fileAway.handleFileAway}
                disabled={!isEditable || !record.canFileAway || fileAway.fileAwayLoading}
              >
                {fileAway.fileAwayLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faArchive} />
                )} File Away
              </button>
              {showFileAwayPatientHint && (
                <div className="file-away-tooltip">Cannot file away - consultation not matched to patient</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toolbar Row 2 - matches document-toolbar */}
      <ConsultationToolbar
        statusEnum={statusEnum}
        recording={recording}
        showAudioPlayer={showAudioPlayer}
        audio={audio}
        navigationContext={navigation.navigationContext}
        onNavigatePrev={navigation.handleNavigatePrev}
        onNavigateNext={navigation.handleNavigateNext}
        prevButtonAnimating={navigation.prevButtonAnimating}
        nextButtonAnimating={navigation.nextButtonAnimating}
        showPatientDiaryToggle={patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR}
        isPatientDiaryVisible={sidebar.isPatientDiaryVisible}
        onTogglePatientDiary={sidebar.togglePatientDiaryVisible}
        hasPatient={Boolean(consultation.patientId)}
      />

      {showRecordingBanners && recording.micInterruptReason && (
        <MicInterruptBanner
          reason={recording.micInterruptReason}
          preferredDeviceId={recording.recoveryDeviceId}
          onDeviceReady={recording.handleMicRecoveryReady}
          onResume={recording.handleMicResume}
          resumeLoading={recording.micResumeLoading}
        />
      )}

      {showRecordingBanners && recording.showUploadFailure && (
        <UploadFailureBanner
          pendingCount={recording.segmentQueue.length}
          quotaExceeded={recording.storageQuotaExceeded}
          retryLoading={recording.uploadRetryLoading}
          onRetry={recording.handleRetryPendingUploads}
        />
      )}

      {/* Content Area */}
      <div className={`consultation-content ${patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR && sidebar.isPatientDiaryVisible ? 'three-panel' : 'two-panel'}`}>
        {/* Main Panel - Notes/Transcript */}
        <div className="main-panel">
          <div className="doc-tabs">
            <div
              className={`doc-tab ${activeMainTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('notes')}
            >
              Notes
            </div>
            <div
              className={`doc-tab ${activeMainTab === 'transcript' ? 'active' : ''}`}
              onClick={() => setActiveMainTab('transcript')}
            >
              Transcript
            </div>
          </div>
          <div className="main-tab-content" ref={sidebar.mainTabContentRef}>
            <div style={{ display: activeMainTab === 'notes' ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
              <NotesTab
                consultation={consultation}
                isEditable={isEditable}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
                onConsultationUpdate={updateConsultation}
              />
            </div>
            <div style={{ display: activeMainTab === 'transcript' ? 'flex' : 'none', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
              <TranscriptTab
                consultation={consultation}
                audioCurrentTime={audio.audioCurrentTime}
                onSeekAudio={audio.handleSeekAudio}
                highlightQuote={sidebar.transcriptQuoteHighlight}
                highlightQuoteKey={sidebar.transcriptQuoteSearchKey}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
              />
            </div>
          </div>
        </div>

        {/* Sidebar Panel - Details/Codes/Measurements/Tasks/Actions */}
        <div className="detail-sidebar">
          <div className="doc-tabs">
            <div
              className={`doc-tab ${activeSidebarTab === 'details' ? 'active' : ''}${sidebar.detailsTabAnimating ? ' doc-tab-animating' : ''}`}
              onClick={() => setActiveSidebarTab('details')}
            >
              Details
            </div>
            {patientDiaryLayout === PATIENT_DIARY_LAYOUT_TAB && (
              <div
                className={`doc-tab ${activeSidebarTab === 'patientDiary' ? 'active' : ''} ${!consultation.patientId ? 'disabled' : ''}`}
                onClick={() => consultation.patientId && setActiveSidebarTab('patientDiary')}
                title={!consultation.patientId ? 'No patient matched' : undefined}
              >
                Patient Diary
                {patient.pendingReferralsCount > 0 && (
                  <span className="notification-badge">{patient.pendingReferralsCount}</span>
                )}
              </div>
            )}
            <div
              className={`doc-tab ${activeSidebarTab === 'existingCodes' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('existingCodes')}
            >
              Existing Codes
            </div>
            <div
              className={`doc-tab ${activeSidebarTab === 'newCodes' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('newCodes')}
            >
              New Codes
            </div>
            <div
              className={`doc-tab ${activeSidebarTab === 'measurements' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('measurements')}
            >
              Measurements
            </div>
            <div
              className={`doc-tab ${activeSidebarTab === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('tasks')}
            >
              Tasks
            </div>
            <div
              className={`doc-tab ${activeSidebarTab === 'actions' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('actions')}
            >
              Actions
            </div>
          </div>
          <div className="tab-content-wrapper" ref={sidebar.sidebarContentRef}>
            <div style={{ display: activeSidebarTab === 'details' ? 'block' : 'none' }}>
              <DetailsTab
                consultation={consultation}
                patientData={patient.patientData}
                patientDataError={patient.patientDataError}
                patientRecordData={patient.patientRecordData}
                isEditable={isEditable}
                onConsultationUpdate={updateConsultation}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
              />
            </div>
            {patientDiaryLayout === PATIENT_DIARY_LAYOUT_TAB && (
              <div style={{ display: activeSidebarTab === 'patientDiary' ? 'block' : 'none' }}>
                <PatientDiaryPanel
                  patientData={patient.patientData}
                  patientDataError={patient.patientDataError}
                  patientRecordData={patient.patientRecordData}
                  patientRecordDataError={patient.patientRecordDataError}
                  doc={consultation}
                  onDocumentClick={sidebar.openDocumentPreview}
                  onReferralClick={referral.handleReferralClick}
                  layoutMode={PATIENT_DIARY_LAYOUT_TAB}
                  onLayoutModeChange={sidebar.handlePatientDiaryLayoutChange}
                  canEdit={isEditable}
                  existingPatientCodes={consultation.existingCodes}
                  onExistingCodeClick={sidebar.handleDiaryExistingCodeClick}
                  onRefreshPatientCodes={patient.handleRefreshPatientCodes}
                  isRefreshingPatientCodes={patient.refreshingPatientCodes}
                />
              </div>
            )}
            <div style={{ display: activeSidebarTab === 'existingCodes' ? 'block' : 'none' }}>
              <CodesTab
                section="existing"
                consultation={consultation}
                isEditable={isEditable}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
                onConsultationUpdate={updateConsultation}
                onTranscriptSearch={sidebar.handleTranscriptSearch}
              />
            </div>
            <div style={{ display: activeSidebarTab === 'newCodes' ? 'block' : 'none' }}>
              <CodesTab
                section="new"
                consultation={consultation}
                isEditable={isEditable}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
                onConsultationUpdate={updateConsultation}
                onTranscriptSearch={sidebar.handleTranscriptSearch}
              />
            </div>
            <div style={{ display: activeSidebarTab === 'measurements' ? 'block' : 'none' }}>
              <MeasurementsTab
                consultation={consultation}
                isEditable={isEditable}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
                onConsultationUpdate={updateConsultation}
                shouldBundleBloodPressureReadings={shouldBundleBloodPressureReadings}
              />
            </div>
            <div style={{ display: activeSidebarTab === 'tasks' ? 'block' : 'none' }}>
              <OutcomeTasksTab
                consultation={consultation}
                isEditable={isEditable}
                isRegenerationEnabled={isRegenerationEnabled}
                handleRegenerationClick={handleRegenerationClick}
                regenerateService={regenerateService}
                onConsultationUpdate={updateConsultation}
                activeUsers={activeUsers}
                teams={teams}
                onTranscriptSearch={sidebar.handleTranscriptSearch}
              />
            </div>
            <div style={{ display: activeSidebarTab === 'actions' ? 'block' : 'none' }}>
              <ActionsTab
                consultation={consultation}
                activeUsers={activeUsers}
                teams={teams}
                patientData={patient.patientData}
                onConsultationUpdate={updateConsultation}
              />
            </div>
          </div>
        </div>

        {/* Patient Diary Panel */}
        {patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR && sidebar.isPatientDiaryVisible && consultation.patientId && (
          <div className="patient-diary-container">
            <PatientDiaryPanel
              patientData={patient.patientData}
              patientDataError={patient.patientDataError}
              patientRecordData={patient.patientRecordData}
              patientRecordDataError={patient.patientRecordDataError}
              doc={consultation}
              onDocumentClick={sidebar.openDocumentPreview}
              onReferralClick={referral.handleReferralClick}
              layoutMode={PATIENT_DIARY_LAYOUT_SIDEBAR}
              onLayoutModeChange={sidebar.handlePatientDiaryLayoutChange}
              canEdit={isEditable}
              existingPatientCodes={consultation.existingCodes}
              onExistingCodeClick={sidebar.handleDiaryExistingCodeClick}
              onRefreshPatientCodes={patient.handleRefreshPatientCodes}
              isRefreshingPatientCodes={patient.refreshingPatientCodes}
            />
          </div>
        )}
      </div>

      <DeleteConsultationModal
        isOpen={deletion.deleteModalOpen}
        onClose={deletion.closeDeleteModal}
        onConfirm={deletion.confirmDelete}
        consultation={consultation}
        patientName={patient.patientName}
        loading={deletion.deleteLoading}
      />

      {fileAway.showFileAwayModal && (
        <FileAwayModal
          doc={consultation}
          entityType="consultation"
          activeUsers={activeUsers}
          teams={teams}
          comms={fileAway.comms}
          onConfirm={fileAway.handleFileAwayConfirm}
          onCancel={fileAway.handleFileAwayCancel}
          isProcessing={fileAway.fileAwayLoading}
          shouldBundleBloodPressureReadings={shouldBundleBloodPressureReadings}
        />
      )}

      {referral.referralModalOpen && (
        <ReferralModal
          isOpen={referral.referralModalOpen}
          onClose={referral.closeReferralModal}
          referral={referral.selectedReferral}
          onMarkComplete={referral.handleReferralMarkComplete}
          onUpdate={referral.handleReferralUpdate}
          onDelete={referral.handleReferralDelete}
          loading={referral.referralModalLoading}
          isReadOnly={!record.hasConsultWrite || !!referral.selectedReferral?.isComplete}
          activeUsers={activeUsers}
          teams={teams}
        />
      )}

      {sidebar.showDocumentPreview && sidebar.previewDocumentId && (
        <DocumentPreviewModal
          isOpen={sidebar.showDocumentPreview}
          onClose={sidebar.closeDocumentPreview}
          documentId={sidebar.previewDocumentId}
          activeUsers={activeUsers}
          teams={teams}
          fromConsultationId={consultationId}
        />
      )}
    </div>
  );
};

export default ConsultationDetailViewController;
